import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface AgingRow {
  bill_id: string;
  company_id: string;
  project_id: string;
  vendor_id: string;
  vendor_name: string;
  project_name: string;
  bill_number: string;
  bill_date: string;
  due_date: string | null;
  total: number;
  status: string;
  days_outstanding: number;
  aging_bucket: string;
}

const BUCKET_CONFIG: Record<string, { label: string; color: string; badgeVariant: string }> = {
  current: { label: "Current (0-30)", color: "text-green-600", badgeVariant: "bg-green-100 text-green-800 border-green-200" },
  "31-60": { label: "31-60 Days", color: "text-yellow-600", badgeVariant: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  "61-90": { label: "61-90 Days", color: "text-orange-600", badgeVariant: "bg-orange-100 text-orange-800 border-orange-200" },
  "90+": { label: "Over 90 Days", color: "text-red-600", badgeVariant: "bg-red-100 text-red-800 border-red-200" },
};

const BUCKET_ORDER = ["current", "31-60", "61-90", "90+"];

export const APAgingReport = () => {
  const isMobile = useIsMobile();
  const [openVendors, setOpenVendors] = useState<Set<string>>(new Set());

  const { data: rows, isLoading } = useQuery({
    queryKey: ["ap_aging"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_ap_aging" as any)
        .select("*")
        .order("days_outstanding", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AgingRow[];
    },
  });

  const bucketSummary = useMemo(() => {
    const sums: Record<string, { count: number; total: number }> = {};
    BUCKET_ORDER.forEach(b => { sums[b] = { count: 0, total: 0 }; });
    (rows ?? []).forEach(r => {
      const b = r.aging_bucket;
      if (sums[b]) {
        sums[b].count++;
        sums[b].total += Number(r.total);
      }
    });
    return sums;
  }, [rows]);

  const vendorGroups = useMemo(() => {
    const map = new Map<string, { vendorName: string; bills: AgingRow[] }>();
    (rows ?? []).forEach(r => {
      const key = r.vendor_id ?? "unknown";
      if (!map.has(key)) map.set(key, { vendorName: r.vendor_name ?? "Unknown", bills: [] });
      map.get(key)!.bills.push(r);
    });
    return Array.from(map.values()).sort((a, b) => {
      const aTotal = a.bills.reduce((s, r) => s + Number(r.total), 0);
      const bTotal = b.bills.reduce((s, r) => s + Number(r.total), 0);
      return bTotal - aTotal;
    });
  }, [rows]);

  const grandTotal = (rows ?? []).reduce((s, r) => s + Number(r.total), 0);

  const toggleVendor = (name: string) => {
    setOpenVendors(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  if (isLoading) return <p className="text-muted-foreground p-4">Loading aging data…</p>;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {BUCKET_ORDER.map(bucket => {
          const cfg = BUCKET_CONFIG[bucket];
          const s = bucketSummary[bucket];
          return (
            <Card key={bucket} className="border">
              <CardHeader className="pb-1 pt-3 px-4">
                <CardTitle className="text-xs font-medium text-muted-foreground">{cfg.label}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3">
                <p className={`text-xl font-bold ${cfg.color}`}>{fmt(s.total)}</p>
                <p className="text-xs text-muted-foreground">{s.count} bill{s.count !== 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Grand total */}
      <div className="flex items-center justify-between px-1">
        <span className="text-sm font-medium text-muted-foreground">Total Outstanding AP</span>
        <span className="text-lg font-bold">{fmt(grandTotal)}</span>
      </div>

      {vendorGroups.length === 0 ? (
        <p className="text-muted-foreground text-sm text-center py-8">No outstanding bills.</p>
      ) : isMobile ? (
        /* Mobile: collapsible vendor cards */
        <div className="space-y-3">
          {vendorGroups.map(group => {
            const vendorTotal = group.bills.reduce((s, r) => s + Number(r.total), 0);
            const isOpen = openVendors.has(group.vendorName);
            return (
              <Collapsible key={group.vendorName} open={isOpen} onOpenChange={() => toggleVendor(group.vendorName)}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardContent className="p-4 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{group.vendorName}</p>
                          <p className="text-xs text-muted-foreground">{group.bills.length} bill{group.bills.length !== 1 ? "s" : ""}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold">{fmt(vendorTotal)}</span>
                          {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 space-y-2 border-t pt-2">
                      {group.bills.map(bill => {
                        const cfg = BUCKET_CONFIG[bill.aging_bucket] ?? BUCKET_CONFIG["90+"];
                        return (
                          <div key={bill.bill_id} className="flex items-start justify-between py-1.5">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs">{bill.bill_number}</span>
                                <Badge variant="outline" className={`text-[10px] ${cfg.badgeVariant}`}>{bill.days_outstanding}d</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{bill.project_name ?? "—"}</p>
                            </div>
                            <span className="text-sm font-medium shrink-0 ml-2">{fmt(Number(bill.total))}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      ) : (
        /* Desktop table */
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill #</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-center">Age</TableHead>
              <TableHead>Bucket</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vendorGroups.map(group => {
              const vendorTotal = group.bills.reduce((s, r) => s + Number(r.total), 0);
              return [
                <TableRow key={`vendor-${group.vendorName}`} className="bg-muted/40">
                  <TableCell colSpan={4} className="font-semibold text-sm">{group.vendorName}</TableCell>
                  <TableCell className="text-right font-semibold text-sm">{fmt(vendorTotal)}</TableCell>
                  <TableCell colSpan={2} className="text-xs text-muted-foreground">
                    {group.bills.length} bill{group.bills.length !== 1 ? "s" : ""}
                  </TableCell>
                </TableRow>,
                ...group.bills.map(bill => {
                  const cfg = BUCKET_CONFIG[bill.aging_bucket] ?? BUCKET_CONFIG["90+"];
                  return (
                    <TableRow key={bill.bill_id}>
                      <TableCell className="font-mono text-xs">{bill.bill_number}</TableCell>
                      <TableCell className="text-sm">{bill.project_name ?? "—"}</TableCell>
                      <TableCell className="text-xs">
                        {bill.bill_date ? format(new Date(bill.bill_date), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {bill.due_date ? format(new Date(bill.due_date), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">{fmt(Number(bill.total))}</TableCell>
                      <TableCell className="text-center text-xs">{bill.days_outstanding}d</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${cfg.badgeVariant}`}>{cfg.label}</Badge>
                      </TableCell>
                    </TableRow>
                  );
                }),
              ];
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};